/* eslint-disable react/jsx-indent */

import React, {useMemo, useState} from 'react';
import {
  DashboardLayout,
  SkeletonLoader,
  SecondaryButton,
} from '../../components';
import VerificationAnalyticsPieChart from '../../components/VerificationAnalyticsPieChart';
import {renderFlowCards, renderStatusDistribution} from './utils';
import {useAllApps} from '../../hooks/useApps';
import VerificationLineChart from './VerificationLineChart';
import {Period} from '../FraudDetection/Reconciliation/components/Period';
import {verificationChartPeriod} from '../FraudDetection/Reconciliation/constants';
import {getCookieValue} from '../../helpers/getCookie';
// import {useMediaQuery} from '../../hooks';
import FilterV2 from 'components/FilterV2';
import {useVerificationsAnalysis} from '../../hooks/useVerificationsAnalysis';
import {dateRanges} from 'helpers/getDateRange';

function VerificationAnalytics() {
  const [date, setDate] = useState('monthly');
  const [showFilter, setShowFilter] = useState(true);
  const {appIds, isLoading: appsLoading} = useAllApps();
  const env = getCookieValue('env');
  const envData = env === 'Live' ? 'production' : env;

  const [filterParams, setFilterParams] = useState({
    date: 'seven_days',
    dateRange: dateRanges.seven_days(),
  });

  const handleFilterChange = values => {
    setFilterParams({
      flows: values.flows,
      dateRange: values.dateRange,
    });
  };

  const params = useMemo(
    () => ({
      app_ids: appIds?.toString(),
      period: date,
      start: filterParams?.dateRange?.start,
      end: filterParams?.dateRange?.end,
      environment: envData?.toLowerCase(),
    }),
    [appIds, date, envData, filterParams],
  );

  const [
    failureReasonQuery,
    cardQuery,
    chartQuery,
    conversionRateQuery,
    percentageQuery,
    countryDistributionQuery,
  ] = useVerificationsAnalysis({
    params,
    enabled: Boolean(!appsLoading && appIds),
  });

  // Destructure data and loading from the individual queries
  const failureReasonData = failureReasonQuery.data;
  const failureReasonDataLoading = failureReasonQuery.isLoading;
  const cardData = cardQuery.data;
  const chartData = chartQuery.data;
  const conversionRateData = conversionRateQuery.data;
  const percentageData = percentageQuery.data;
  const countryDistributionData = countryDistributionQuery.data;

  // =========================================================================
  // useVerificationsAnalysis IMPLEMENTATION
  // =========================================================================

  // Consolidate data into a single object for easier access (optional)
  const ANALYSIS_DATA = useMemo(
    () => ({
      cards: cardData?.data?.cards,
      error_distribution: failureReasonData?.data?.error_distribution,
      monthly_chart: chartData?.data?.monthly_chart,
      conversion_rate: conversionRateData?.data?.conversion_rate,
      percentage: percentageData?.data?.percentage,
      country_distribution: countryDistributionData?.data?.country_distribution,
    }),
    [
      cardData?.data,
      failureReasonData?.data,
      chartData?.data,
      conversionRateData?.data,
      percentageData?.data,
      countryDistributionData?.data,
    ],
  );

  const topCountries = useMemo(
    () =>
      ANALYSIS_DATA?.country_distribution
        ?.filter(country => country.country !== 'Unknown')
        .sort((a, b) => b.percentage - a.percentage)
        .slice(0, 5),
    [ANALYSIS_DATA?.country_distribution],
  );

  const topFailureReasons = useMemo(() => {
    const FAILURE_REASONS_DATA = ANALYSIS_DATA?.error_distribution || [];

    const TOP_N = 4;
    const topItems = FAILURE_REASONS_DATA?.slice(0, TOP_N) || [];
    const otherItems = FAILURE_REASONS_DATA?.slice(TOP_N) || [];

    let othersCount = 0;
    let othersPercentage = 0;

    for (const item of otherItems) {
      othersCount += item.verification_count;
      othersPercentage += item.percentage;
    }

    const othersItem = {
      review_comment: 'OTHERS',
      verification_count: othersCount,
      percentage: parseFloat(othersPercentage.toFixed(1)),
    };
    // Use the combined loading state
    return failureReasonDataLoading ? [] : [...topItems, othersItem];
  }, [ANALYSIS_DATA?.error_distribution, failureReasonDataLoading]);

  const toggleFilterVisibility = () => {
    setShowFilter(prevShowFilter => !prevShowFilter);
  };

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">
          Verification Analytics
        </p>
      }
    >
      <div className="flex items-center py-2 flex-wrap gap-3 justify-end mx-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SecondaryButton buttonText="Filter" onClick={toggleFilterVisibility} />
      </div>

      <div className={`filter-slide-container ${showFilter ? 'open' : ''}`}>
        <FilterV2
          onFilterChange={handleFilterChange}
          initialValues={filterParams}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2 lg:gap-4 flex-wrap mt-5">
        {renderFlowCards({
          cards: ANALYSIS_DATA?.cards,
          isLoading: cardQuery.isLoading,
          conversionRate: ANALYSIS_DATA?.conversion_rate,
        })}
      </div>

      <div className="flex items-center flex-col md:flex-row gap-4 md:h-[250px] mt-6">
        {/* Status Distribution Block */}
        <div className="bg-white shadow-cards w-full md:w-[50%] xl:w-[33%] h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
          <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
            <p className="font-medium text-sm xl:text-base text-deepBlue">
              Status Distribution
            </p>
          </div>

          <div className="flex justify-between h-[80%]">
            <div className="w-[50%]">
              <VerificationAnalyticsPieChart
                verifications={ANALYSIS_DATA?.cards}
                isLoading={cardQuery.isLoading}
              />
            </div>
            <div className="w-[50%] px-6 border border-transparent border-l-[#F2F2F2] flex items-center justify-center flex-col gap-4  ">
              {renderStatusDistribution({stats: ANALYSIS_DATA?.percentage})}
            </div>
          </div>
        </div>

        {/* Country Distribution Block */}
        <div className="bg-white shadow-cards w-full md:w-[50%] xl:w-[33%] h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
          <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
            <p className="font-medium text-sm xl:text-base text-deepBlue">
              Country Distribution
            </p>
          </div>

          {countryDistributionQuery.isLoading ? (
            <div className="flex items-start flex-col w-full gap-3 mt-6 px-6">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="w-full">
                  <div className="flex justify-between items-center">
                    <SkeletonLoader
                      style={{width: '80%', height: 13, borderRadius: 6}}
                    />
                    <SkeletonLoader style={{width: 30, height: 16}} />
                  </div>
                </div>
              ))}
            </div>
          ) : !countryDistributionQuery.isLoading &&
            topCountries &&
            topCountries.length === 0 ? (
            <div className="flex items-center justify-center w-full h-full">
              <p className="text-[#464646] font-medium text-sm capitalize   tracking-[-0.5px]">
                No Country Distribution found
              </p>
            </div>
          ) : (
            <div className="flex items-start gap-4 px-6 py-2">
              <div className="flex gap-[15px] w-[48%] flex-col items-start">
                {topCountries &&
                  topCountries?.map((country, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {country.flag ? (
                        <img
                          src={country.flag}
                          alt={country.country || 'country flag'}
                          className="min-w-[24px] min-h-[24px] rounded-full w-6 h-6 object-cover"
                        />
                      ) : (
                        <div className="min-w-[24px] min-h-[24px] w-6 h-6 rounded-full bg-[#E9EBF0]   flex items-center justify-center text-batchBlue font-bold">
                          {country.country.charAt(0) || 'N/A'}
                        </div>
                      )}
                      <p className="text-[#464646] font-medium text-xs uppercase   tracking-[-0.5px]">
                        {country.country || 'N/A'}
                      </p>
                    </div>
                  ))}
              </div>

              <div className="flex items-start flex-col w-full gap-[15px]">
                {topCountries &&
                  topCountries?.map((country, index) => (
                    <div key={index} className="w-full">
                      <div className="flex justify-between items-center h-[24px]">
                        <div className="w-[80%] h-[13px] bg-[#E9EBF0] rounded overflow-hidden">
                          <div
                            style={{width: `${country.percentage}%`}}
                            className="h-full bg-batchBlue rounded transition-all duration-200 ease-out"
                          />
                        </div>
                        <p className="text-[#858daa] font-semibold text-sm ml-2">
                          {country.percentage.toFixed(1) || 0}%
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Top Failure Reasons Block */}
        <div className="bg-white shadow-cards w-full md:w-[50%] xl:w-[33%] h-full rounded-xl border-[0.5px] border-[#EFEFEF]">
          <div className="bg-[#F9F9F9] py-3 px-6 rounded-t-xl">
            <p className="font-medium text-sm xl:text-base text-deepBlue">
              Top Failure Reasons
            </p>
          </div>
          {failureReasonDataLoading ? (
            <div className="flex items-start flex-col w-full gap-3 mt-6 px-6">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="w-full">
                  <div className="flex justify-between items-center">
                    <SkeletonLoader
                      style={{width: '80%', height: 13, borderRadius: 6}}
                    />
                    <SkeletonLoader style={{width: 30, height: 16}} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-start flex-col w-full gap-5 mt-2 px-6">
              {topFailureReasons &&
                topFailureReasons?.map((reason, index) => (
                  <div key={index} className="w-full">
                    <div className="flex justify-between items-center ">
                      <div className="group relative">
                        <p className="text-body uppercase text-[12px]   tracking-[-0.5px] truncate max-w-[200px] md:max-w-full">
                          {reason.review_comment &&
                          reason.review_comment.length > 34
                            ? `${reason.review_comment.substring(0, 34)}...`
                            : reason.review_comment}
                        </p>

                        {reason.review_comment &&
                          reason.review_comment.length > 34 && (
                            <div className="hidden group-hover:block w-[362px] bg-grey-50 text-[#5C688E] h-fit absolute bottom-full text-left z-10 top-[25px] right-0 text-[12px] rounded p-4 shadow-lg">
                              {reason.review_comment}
                            </div>
                          )}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-[#464646] font-semibold text-sm ml-2">
                          {reason?.percentage.toFixed(1) || 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
      <VerificationLineChart
        customElements={
          <Period
            date={date}
            setDate={setDate}
            otherChartPeriod={verificationChartPeriod}
          />
        }
        chartData={ANALYSIS_DATA?.monthly_chart}
        periodType={date}
        loading={chartQuery.isLoading}
      />
    </DashboardLayout>
  );
}

export default VerificationAnalytics;
